/**
 * The app's dropdown, drawn in the theme for filter bars and forms alike: a field-like button that
 * opens a themed list of options instead of the browser's own picker.
 *
 * It follows the WAI-ARIA select-only combobox: focus stays on the button while the arrow keys,
 * Page Up/Down and Home/End move the highlight (typing a letter jumps to an option), Enter or Space
 * picks, Escape closes (without closing a dialog it sits in) and Tab moves on. A list longer than
 * 10 gets a search box. The list opens in the browser's top layer (the popover API), so a dialog's
 * clipping or the page's fade-in transform can't cut it off or misplace it (RESP-18); it opens
 * above the field when there's more room there, and follows the field as the page scrolls.
 *
 * With `name` or `required`, a hidden native <select> carries the value into the form and takes
 * autofill. A required one left empty marks the field and opens its list when the form is
 * submitted, in place of the browser's bubble.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";

import { moveHighlight, placeList, searchOptions, typeahead } from "../../lib/listbox";
import type { ListPlacement } from "../../lib/listbox";

export interface SelectOption<T extends string | number = string> {
  value: T;
  label: string;
  /** A second, muted line under the label (also searched). */
  hint?: string;
  /** Searched but not shown, e.g. a state's code. */
  keywords?: string;
  disabled?: boolean;
}

// The top layer (Chrome 114, Safari 17, Firefox 125). An older browser gets the list portalled to
// <body>, above the dialogs (z-50).
const TOP_LAYER = typeof HTMLElement !== "undefined" && "showPopover" in HTMLElement.prototype;
/** Lists longer than this get a search box. */
const SEARCH_FROM = 10;
/** Letters typed within this many ms of each other make one type-to-jump string. */
const TYPING_MS = 600;

// How the page was last pressed ("mouse", "touch", "pen"), the field's label included. After a
// tap, a list's search box waits to be tapped: focusing it would raise the keyboard over the list.
let lastPointer = "";
if (typeof document !== "undefined") {
  document.addEventListener("pointerdown", (e) => (lastPointer = e.pointerType), { capture: true, passive: true });
}

const isTyping = (e: ReactKeyboardEvent) => e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey && !e.altKey;

export function Select<T extends string | number>({
  value,
  options,
  onChange,
  placeholder = "Select…",
  size = "sm",
  className = "",
  searchable = options.length > SEARCH_FROM,
  disabled = false,
  invalid = false,
  required = false,
  name,
  id,
  onBlur,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  "data-testid": testId,
}: {
  value: T;
  options: readonly SelectOption<T>[];
  onChange: (value: T) => void;
  /** Shown, muted, while the value matches no option. */
  placeholder?: string;
  /** "sm": compact, in the text size of where it sits (filter bars, tables, small forms); "md": form size, like `.input`. */
  size?: "sm" | "md";
  /** Layout, size and error tweaks on the field: w-full, flex-1, rounded-*, py-*, pl-*, text-*, border-*. */
  className?: string;
  /** A search box over the options; on by default for lists longer than 10. */
  searchable?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  /** Once focus leaves the field and its list. */
  onBlur?: () => void;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "data-testid"?: string;
}) {
  const uid = useId();
  const listId = `${uid}list`;
  const optionId = (i: number) => `${uid}o${i}`;
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const typed = useRef({ text: "", at: 0 });
  const focusSearch = useRef(false);
  const centre = useRef(false);
  /** The list's own (shrink-to-fit) width, measured when it opens. */
  const fitWidth = useRef(0);
  /** The side of its field the list opened on. */
  const side = useRef<ListPlacement["side"] | undefined>(undefined);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [place, setPlace] = useState<ListPlacement | null>(null);
  const [missing, setMissing] = useState(false);

  const isOpen = open && !disabled;
  const chosen = options.findIndex((o) => o.value === value);
  const shown = searchable ? searchOptions(options, query) : options;
  const showMissing = missing && chosen < 0;

  /** Opens the list on the chosen option (or the first or last); `toSearch` puts the focus in its search box. */
  const show = (start: "chosen" | "first" | "last", toSearch: boolean) => {
    if (disabled) return;
    setQuery("");
    setActive(start === "chosen" && chosen >= 0 && !options[chosen].disabled ? chosen : moveHighlight(options, -1, start === "last" ? -1 : 1));
    focusSearch.current = searchable && toSearch;
    centre.current = true;
    fitWidth.current = 0;
    side.current = undefined;
    setPlace(null);
    setOpen(true);
  };
  const hide = (refocus: boolean) => {
    setOpen(false);
    setQuery("");
    if (refocus) button.current?.focus();
  };
  const pick = (i: number) => {
    const o = shown[i];
    if (!o || o.disabled) return;
    setMissing(false);
    if (o.value !== value) onChange(o.value);
    hide(true);
  };
  const jump = (letter: string, from: number) => {
    const now = Date.now();
    const t = typed.current;
    t.text = now - t.at < TYPING_MS ? t.text + letter : letter;
    t.at = now;
    const i = typeahead(shown, t.text, from);
    if (i >= 0) setActive(i);
  };
  /** Focus going to `to` leaves the field: not to its button or anywhere in its list. */
  const leaves = (to: EventTarget | null) =>
    !(to instanceof Node && (button.current?.contains(to) || panel.current?.contains(to)));
  const blurred = (to: EventTarget | null) => {
    if (!leaves(to)) return;
    if (open) {
      setOpen(false);
      setQuery("");
    }
    onBlur?.();
  };

  const onKeys = (e: ReactKeyboardEvent<HTMLElement>, inSearch: boolean) => {
    const k = e.key;
    if (!isOpen) {
      if (k === "ArrowDown" || k === "ArrowUp" || k === "Enter" || k === " ") {
        e.preventDefault();
        show("chosen", true);
      } else if (k === "Home" || k === "End") {
        e.preventDefault();
        show(k === "Home" ? "first" : "last", true);
      } else if (isTyping(e)) {
        e.preventDefault();
        show("chosen", true);
        if (searchable) {
          // The letter starts the search instead.
          setQuery(e.key);
          setActive(moveHighlight(searchOptions(options, e.key), -1, 1));
        } else jump(e.key, chosen);
      }
      return;
    }
    switch (k) {
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        if (k === "ArrowUp" && e.altKey) pick(active);
        else setActive(moveHighlight(shown, active, k === "ArrowDown" ? 1 : -1));
        break;
      case "PageDown":
      case "PageUp":
        e.preventDefault();
        setActive(moveHighlight(shown, active, k === "PageDown" ? 10 : -10));
        break;
      case "Home":
      case "End":
        if (inSearch) break; // the search box's caret
        e.preventDefault();
        setActive(moveHighlight(shown, -1, k === "Home" ? 1 : -1));
        break;
      case "Enter":
        e.preventDefault(); // also keeps a form from submitting
        if (active >= 0) pick(active);
        else hide(true);
        break;
      case " ":
        if (inSearch) break;
        e.preventDefault();
        if (active >= 0) pick(active);
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation(); // a dialog closes on Escape too: only the list should
        hide(true);
        break;
      // Tab moves on as usual; the list closes once the focus has left it (see `blurred`).
      default:
        if (!inSearch && isTyping(e)) {
          e.preventDefault();
          jump(e.key, active);
        }
    }
  };

  /** Puts the list against its field, in viewport pixels; closes it once the field is out of sight. */
  const reposition = useCallback(() => {
    const field = button.current;
    const el = panel.current;
    if (!field || !el) return;
    const r = field.getBoundingClientRect();
    const vv = window.visualViewport;
    const page = document.documentElement;
    // The visible part: a phone's keyboard shrinks the visual viewport, not the layout one.
    const viewport = { width: page.clientWidth, height: vv ? Math.min(page.clientHeight, vv.offsetTop + vv.height) : page.clientHeight };
    if (r.width === 0 || r.bottom < 0 || r.top > viewport.height) {
      setOpen(false);
      setQuery("");
      return;
    }
    // The list may be wider than its field, to fit its longest option on one line (up to 22rem,
    // plus room for a scrollbar). Its height is measured at that width, so wrapping is counted.
    if (!fitWidth.current) fitWidth.current = el.offsetWidth;
    const fit = { minWidth: Math.min(Math.max(128, fitWidth.current + 12), 352) };
    el.style.width = `${placeList(r, viewport, { height: 0, ...fit }).width}px`;
    const rows = list.current;
    const natural = el.offsetHeight - (rows?.clientHeight ?? 0) + (rows?.scrollHeight ?? 0);
    const placed = placeList(r, viewport, { height: natural, ...fit, keep: side.current });
    side.current = placed.side;
    setPlace(placed);
  }, []);

  // Opening: into the top layer, placed, and kept with its field while anything scrolls.
  useLayoutEffect(() => {
    const el = panel.current;
    if (!isOpen || !el) return;
    if (TOP_LAYER) {
      el.setAttribute("popover", "manual");
      el.showPopover();
    }
    reposition();
    let frame = 0;
    const follow = (e: Event) => {
      if (e.target instanceof Node && el.contains(e.target)) return; // the list's own scrolling
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(reposition);
    };
    const outside = (e: PointerEvent) => {
      // The field's own label clicks its button, which toggles the list: not "outside".
      const own = button.current?.closest("label") ?? button.current;
      if (e.target instanceof Node && (el.contains(e.target) || own?.contains(e.target))) return;
      setOpen(false);
      setQuery("");
    };
    window.addEventListener("resize", follow);
    window.addEventListener("scroll", follow, true);
    window.visualViewport?.addEventListener("resize", follow);
    document.addEventListener("pointerdown", outside, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", follow);
      window.removeEventListener("scroll", follow, true);
      window.visualViewport?.removeEventListener("resize", follow);
      document.removeEventListener("pointerdown", outside, true);
      if (TOP_LAYER && el.isConnected && el.matches(":popover-open")) el.hidePopover();
    };
  }, [isOpen, reposition]);

  // The search narrowing the list changes its height: refit it.
  useLayoutEffect(() => {
    if (isOpen) reposition();
  }, [isOpen, shown.length, reposition]);

  // Once the list is placed, and so visible (a hidden element can't take the focus): give its search
  // box the focus if it should have it, keep the highlighted option in view, and on opening centre
  // the chosen one.
  useLayoutEffect(() => {
    if (!isOpen || !place) return;
    if (focusSearch.current) {
      focusSearch.current = false;
      search.current?.focus({ preventScroll: true });
    }
    const rows = list.current;
    const row = rows?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    if (!rows || !row) return;
    if (centre.current) {
      centre.current = false;
      rows.scrollTop = row.offsetTop - (rows.clientHeight - row.offsetHeight) / 2;
    } else if (row.offsetTop < rows.scrollTop) rows.scrollTop = row.offsetTop - 4;
    else if (row.offsetTop + row.offsetHeight > rows.scrollTop + rows.clientHeight) {
      rows.scrollTop = row.offsetTop + row.offsetHeight - rows.clientHeight + 4;
    }
  }, [isOpen, place, active]);

  // A failed submit marked the empty field: once it has a value, a later empty one waits for the next submit.
  useEffect(() => {
    if (chosen >= 0) setMissing(false);
  }, [chosen]);

  const activeId = isOpen && shown[active] ? optionId(active) : undefined;
  const panelEl = (
    <div
      ref={panel}
      data-side={place?.side}
      // Keep the focus where it is (button or search box) while an option is pressed…
      onMouseDown={(e) => {
        if (e.target !== search.current) e.preventDefault();
      }}
      // …and stop a click in here reaching a wrapping <label>, which would click the button again.
      onClick={(e) => e.preventDefault()}
      style={
        place
          ? { top: place.top, left: place.left, right: "auto", bottom: "auto", width: place.width, maxHeight: place.maxHeight }
          : { top: 0, left: 0, right: "auto", bottom: "auto", visibility: "hidden" }
      }
      className={`fixed z-[60] m-0 flex flex-col overflow-hidden rounded-xl border border-line bg-surface p-0 text-left text-sm font-normal normal-case tracking-normal text-ink shadow-raised animate-pop-in motion-reduce:animate-none ${
        place?.side === "above" ? "origin-bottom" : "origin-top"
      }`}
    >
      {searchable && (
        <div className="shrink-0 border-b border-line p-1.5">
          <div className="relative">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={search}
              type="text"
              role="combobox"
              aria-label="Search the list"
              aria-controls={listId}
              aria-expanded="true"
              aria-autocomplete="list"
              aria-activedescendant={activeId}
              autoComplete="off"
              spellCheck={false}
              value={query}
              placeholder="Search…"
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(moveHighlight(searchOptions(options, e.target.value), -1, 1));
              }}
              onKeyDown={(e) => onKeys(e, true)}
              onBlur={(e) => blurred(e.relatedTarget)}
              className="w-full rounded-lg border border-line bg-sunken py-1.5 pl-8 pr-2 text-sm text-ink placeholder:text-ink-faint focus:border-brand focus:bg-surface focus:outline-hidden focus:ring-2 focus:ring-brand/20"
            />
          </div>
        </div>
      )}
      <div
        ref={list}
        id={listId}
        role="listbox"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
      >
        {shown.map((o, i) => {
          const isChosen = o.value === value;
          return (
            <div
              key={String(o.value)}
              id={optionId(i)}
              role="option"
              aria-selected={isChosen}
              aria-disabled={o.disabled || undefined}
              data-index={i}
              data-value={String(o.value)}
              onMouseMove={() => {
                if (!o.disabled && i !== active) setActive(i);
              }}
              onClick={() => pick(i)}
              className={`flex select-none items-start gap-2 rounded-lg px-2.5 py-2 pointer-coarse:py-2.5 ${
                o.disabled ? "cursor-not-allowed text-ink-faint" : "cursor-pointer"
              } ${i === active && !o.disabled ? "bg-brand-50 text-brand" : ""} ${isChosen ? "font-medium" : ""}`}
            >
              <span className="min-w-0 flex-1 break-words">
                {o.label}
                {o.hint && <span className="block text-xs font-normal text-ink-muted">{o.hint}</span>}
              </span>
              {isChosen && (
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true">
                  <path d="m5 10.5 3.2 3.2L15 7" />
                </svg>
              )}
            </div>
          );
        })}
        {shown.length === 0 && <div className="px-2.5 py-2 text-ink-muted">{query.trim() ? `Nothing matches “${query.trim()}”.` : "Nothing to choose."}</div>}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={button}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        aria-activedescendant={activeId}
        aria-invalid={invalid || showMissing || undefined}
        aria-required={required || undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        data-testid={testId}
        value={String(value)}
        disabled={disabled}
        onClick={() => (isOpen ? hide(true) : show("chosen", lastPointer !== "touch"))}
        onKeyDown={(e) => onKeys(e, false)}
        // Space picks on key down; its key up would click the button and open the list again.
        onKeyUp={(e) => {
          if (e.key === " ") e.preventDefault();
        }}
        onBlur={(e) => blurred(e.relatedTarget)}
        className={`${size === "md" ? "select" : "select-sm"} ${showMissing ? "input-error" : ""} ${className}`}
      >
        <span className={`block truncate ${chosen >= 0 ? "" : "text-ink-faint"}`}>{chosen >= 0 ? options[chosen].label : placeholder}</span>
      </button>
      {(name || required) && (
        <select
          aria-hidden="true"
          tabIndex={-1}
          name={name}
          required={required}
          disabled={disabled}
          value={chosen >= 0 ? String(value) : ""}
          // Autofill writes here: hand the value on.
          onChange={(e) => {
            const o = options.find((x) => String(x.value) === e.target.value);
            if (o && !o.disabled) onChange(o.value);
          }}
          onFocus={() => button.current?.focus()}
          onInvalid={(e) => {
            // The theme's "choose one" instead of the browser's bubble: mark the field, and when it
            // is the form's first problem, focus it and open its list.
            e.preventDefault();
            setMissing(true);
            const first = e.currentTarget.form?.querySelector(":is(input, select, textarea):invalid");
            if (!first || first === e.currentTarget) {
              button.current?.focus();
              show("chosen", false);
            }
          }}
          className="sr-only"
        >
          {!options.some((o) => o.value === "") && <option value="" />}
          {options.map((o) => (
            <option key={String(o.value)} value={String(o.value)} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      {isOpen && (TOP_LAYER ? panelEl : createPortal(panelEl, document.body))}
    </>
  );
}
